// ============================================================
// lib/pricing/data.ts
// Language-neutral pricing data, calc config, and compute logic.
// All display labels live in messages/pricing/{locale}.json (PricingPage namespace).
// ============================================================

// ===== TYPES =====
export type PlanId = 'start' | 'compliance' | 'enterprise';
export type SmsRoute = 'ru' | 'global';
export type ScreenCellKey =
	| 'baseAml'
	| 'notIncluded'
	| 'included'
	| 'addon'
	| 'custom'
	| 'no';

// ===== RATES =====
export const RATES: Record<
	'start' | 'compliance',
	{ readonly min: number; readonly rate: number }
> = {
	start: { min: 99, rate: 0.95 },
	compliance: { min: 249, rate: 1.35 },
};

// ===== PLAN METADATA (language-neutral) =====
export const PLANS: ReadonlyArray<{
	readonly id: PlanId;
	readonly code: string;
	readonly badge: string;
	readonly price: string;
}> = [
	{ id: 'start', code: 'PLAN.01', badge: 'STANDARD', price: '99' },
	{ id: 'compliance', code: 'PLAN.02', badge: 'REGULATED', price: '249' },
	{ id: 'enterprise', code: 'PLAN.03', badge: 'CUSTOM', price: 'Custom' },
];

// ===== CALCULATOR CONFIG =====
export const CALC = {
	applicants: { min: 0, max: 50_000, step: 100 },
	presets: [50, 150, 1_000, 5_000, 25_000] as const,
	kyb: {
		min: 499,
		rate: 3.2,
		companies: { min: 0, max: 5_000, step: 25 },
	},
	addons: {
		whiteLabel: { monthly: 1_000, setup: 5_000 },
		extSupport: { monthly: 2_500 },
		smsOtp: { ru: 0.15, global: 0.35 } as Record<SmsRoute, number>,
	},
	annual: { multiplier: 10 }, // 2 months free
	burnMeter: { cells: 24 },
} as const;

// ===== NUMBER FORMATTER =====
// Groups thousands by space (mirrors design's nf() using ru-RU locale + NBSP→space).
export function nf(n: number): string {
	return Math.round(n)
		.toLocaleString('ru-RU')
		.replace(/\u00A0/g, ' ');
}

export function formatUsd(n: number): string {
	return nf(n) + ' USD';
}

// ===== COMPUTE ESTIMATE =====
interface EstimateInput {
	plan: PlanId;
	applicants: number;
	kyb: boolean;
	companies: number;
	whiteLabel: boolean;
	extSupport: boolean;
	smsOtp: boolean;
	smsRoute: SmsRoute;
	annual: boolean;
}

interface EstimateResult {
	isEnt: boolean;
	planPart: number;
	kybPart: number;
	smsPart: number;
	wlPart: number;
	esPart: number;
	monthly: number;
	annualMonthly: number;
	annualSave: number;
	oneTime: number;
	quota: number;
	burnN: number;
	overN: number;
	/** Number of filled cells (0–NCELL) for the burn-meter bar. */
	usedCells: number;
	minApplied: boolean;
	kybMinApplied: boolean;
}

function getRates(plan: PlanId): {
	readonly min: number;
	readonly rate: number;
} {
	if (plan === 'enterprise') return RATES.start;
	return RATES[plan];
}

/** Port of the design's renderVals() compute block — verbatim logic. */
export function computeEstimate(input: EstimateInput): EstimateResult {
	const {
		plan,
		applicants,
		kyb,
		companies,
		whiteLabel,
		extSupport,
		smsOtp,
		smsRoute,
	} = input;
	const isEnt = plan === 'enterprise';
	const R = getRates(plan);

	const usagePart = isEnt ? 0 : applicants * R.rate;
	const planPart = isEnt ? 0 : Math.max(R.min, usagePart);
	const minApplied = !isEnt && usagePart < R.min;

	const kybRaw = companies * CALC.kyb.rate;
	const kybPart = kyb ? Math.max(CALC.kyb.min, kybRaw) : 0;
	const kybMinApplied = kyb && kybRaw < CALC.kyb.min;

	const smsPart =
		!isEnt && smsOtp ? applicants * CALC.addons.smsOtp[smsRoute] : 0;
	const wlPart = whiteLabel ? CALC.addons.whiteLabel.monthly : 0;
	const esPart = extSupport ? CALC.addons.extSupport.monthly : 0;

	const monthly = planPart + kybPart + smsPart + wlPart + esPart;
	const annualMonthly = monthly * CALC.annual.multiplier;
	const annualSave = monthly * 2;
	const oneTime = whiteLabel ? CALC.addons.whiteLabel.setup : 0;

	const quota = isEnt ? 0 : Math.round(R.min / R.rate);
	const burnN = Math.max(0, quota - applicants);
	const overN = Math.max(0, applicants - quota);
	const NCELL = CALC.burnMeter.cells;
	const usedCells =
		quota > 0
			? Math.max(0, Math.min(NCELL, Math.round((applicants / quota) * NCELL)))
			: NCELL;

	return {
		isEnt,
		planPart,
		kybPart,
		smsPart,
		wlPart,
		esPart,
		monthly,
		annualMonthly,
		annualSave,
		oneTime,
		quota,
		burnN,
		overN,
		usedCells,
		minApplied,
		kybMinApplied,
	};
}

// ===== TABLE ROW DATA =====
// Codes and IDs are language-neutral; label keys reference PricingPage namespace.

/** Ordered IDs for SYS.03 grid rows — look up labels via PricingPage.grid.rows.{id}.* */
export const GRID_ROW_IDS = [
	'forWhom',
	'minimum',
	'perApplicant',
	'annual',
	'support',
] as const;
export type GridRowId = (typeof GRID_ROW_IDS)[number];

/** Core feature items (SYS.04) — `key` is i18n-safe, `code` is displayed. */
export const FEATURE_ITEMS = [
	{ key: 'FT_01', code: 'FT.01' },
	{ key: 'FT_02', code: 'FT.02' },
	{ key: 'FT_03', code: 'FT.03' },
	{ key: 'FT_04', code: 'FT.04' },
	{ key: 'FT_05', code: 'FT.05' },
	{ key: 'FT_06', code: 'FT.06' },
	{ key: 'FT_07', code: 'FT.07' },
	{ key: 'FT_08', code: 'FT.08' },
	{ key: 'FT_09', code: 'FT.09' },
] as const;

/** SYS.05 screening rows — cell values are ScreenCellKey enum members. */
interface ScreenRow {
	readonly id: string;
	readonly start: ScreenCellKey;
	readonly comp: ScreenCellKey;
	readonly ent: ScreenCellKey;
}

export const SCREEN_ROWS: ReadonlyArray<ScreenRow> = [
	{ id: 'aml', start: 'baseAml', comp: 'notIncluded', ent: 'included' },
	{ id: 'contour', start: 'baseAml', comp: 'notIncluded', ent: 'included' },
	{ id: 'ofac', start: 'baseAml', comp: 'notIncluded', ent: 'custom' },
	{ id: 'smsOtp', start: 'addon', comp: 'addon', ent: 'custom' },
	{ id: 'kyb', start: 'addon', comp: 'addon', ent: 'custom' },
	{ id: 'kycKybBundle', start: 'addon', comp: 'addon', ent: 'custom' },
	{ id: 'whiteLabel', start: 'no', comp: 'addon', ent: 'custom' },
	{ id: 'customerStorage', start: 'no', comp: 'addon', ent: 'custom' },
];

/** Ordered IDs for SYS.06 add-on rows — labels via PricingPage.addonTable.rows.{id}.* */
export const ADDON_ROW_IDS = [
	'smsRu',
	'smsGlobal',
	'wlSetup',
	'wlMonthly',
	'customerStorage',
	'regionalStorage',
	'extSupport',
] as const;
export type AddonRowId = (typeof ADDON_ROW_IDS)[number];

/** KYB included items (SYS.07) — `key` is i18n-safe, `code` is displayed. */
export const KYB_INCLUDED_ITEMS = [
	{ key: 'KYB_01', code: 'KYB.01' },
	{ key: 'KYB_02', code: 'KYB.02' },
	{ key: 'KYB_03', code: 'KYB.03' },
	{ key: 'KYB_04', code: 'KYB.04' },
	{ key: 'KYB_05', code: 'KYB.05' },
	{ key: 'KYB_06', code: 'KYB.06' },
] as const;

/** KYB extended option IDs — labels via PricingPage.kybSection.extended.{id}.* */
export const KYB_EXTENDED_IDS = [
	'extSupport',
	'finCheck',
	'kycKybBundle',
	'uboKyc',
] as const;
export type KybExtendedId = (typeof KYB_EXTENDED_IDS)[number];
